import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Shield, Crown, Gem, Check, Sparkles, Zap, Monitor, Heart, Rocket, ShieldCheck, ExternalLink, FileText, Gift, Clock, PartyPopper, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useWelcomeSale } from '../../contexts/WelcomeSaleContext';
import { apiPost, apiGet, apiFetch } from '../../services/api';

interface CommunityPassOption {
  id: string;
  name: string;
  duration: string;
  durationLabel: string;
  price: number;
  priceLabel: string;
  popular?: boolean;
  recommended?: boolean;
  badge?: string;
  theme: { gradient: string; accent: string; border: string; glow: string; text: string; icon: any };
}

export const INDIAN_STATES = [
  'Bihar',
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

const communityPassOptions: CommunityPassOption[] = [
  { id: 'monthly', name: 'Silver Pass', duration: '1 Month', durationLabel: '30 Days', price: 39, priceLabel: '₹39', badge: 'Essential', theme: { gradient: 'from-slate-200 to-slate-400', accent: 'text-slate-600 dark:text-slate-400', border: 'border-slate-300 dark:border-white/10', glow: 'shadow-slate-500/10 dark:shadow-white/5', text: 'text-text-main', icon: Shield } },
  { id: 'half-yearly', name: 'Gold Pass', duration: '6 Months', durationLabel: '180 Days', price: 179, priceLabel: '₹179', popular: true, badge: 'Most Popular', theme: { gradient: 'from-amber-200 to-amber-500', accent: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30', glow: 'shadow-amber-500/15', text: 'text-amber-950 dark:text-amber-50', icon: Crown } },
  { id: 'yearly', name: 'Platinum Pass', duration: '1 Year', durationLabel: '365 Days', price: 299, priceLabel: '₹299', badge: 'Best Value', theme: { gradient: 'from-indigo-200 to-indigo-500', accent: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-500/30', glow: 'shadow-indigo-500/15', text: 'text-indigo-950 dark:text-indigo-50', icon: Zap } },
  { id: 'lifetime', name: 'Diamond Pass', duration: 'Lifetime', durationLabel: 'Forever Access', price: 789, priceLabel: '₹789', badge: 'One-time Payment', recommended: true, theme: { gradient: 'from-cyan-200 to-blue-500', accent: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-500/40', glow: 'shadow-cyan-500/20', text: 'text-cyan-950 dark:text-cyan-50', icon: Gem } },
];

const PLAN_RANKS: Record<string, number> = { 'monthly': 1, 'half-yearly': 2, 'yearly': 3, 'lifetime': 4 };

// ─── PricingCard (standalone so hooks are not inside a .map()) ───────────────
interface PricingCardProps {
  option: CommunityPassOption;
  index: number;
  isActive: boolean;
  isUpgrade: boolean;
  isDowngrade: boolean;
  discount: number;
  isLoading: boolean;
  selectedState: string;
  onStateChange: (state: string) => void;
  onSelect: (id: string) => void;
  welcomeSaleActive?: boolean;
  welcomeSalePrice?: number;
  welcomeSaleSavings?: number;
}

const PricingCard: React.FC<PricingCardProps> = ({
  option,
  index,
  isActive,
  isUpgrade,
  isDowngrade,
  discount,
  isLoading,
  selectedState,
  onStateChange,
  onSelect,
  welcomeSaleActive,
  welcomeSalePrice,
  welcomeSaleSavings,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-1, 1], [15, -15]);
  const rotateY = useTransform(mouseX, [-1, 1], [-15, 15]);
  const glowX = useTransform(mouseX, [-1, 1], [-20, 20]);
  const glowY = useTransform(mouseY, [-1, 1], [-20, 20]);

  // Welcome sale takes priority over upgrade discount when active
  const basePrice = welcomeSaleActive && welcomeSalePrice !== undefined
    ? welcomeSalePrice
    : Math.max(0, option.price - discount);
  const effectivePrice = basePrice;
  const totalSavings = welcomeSaleActive && welcomeSaleSavings !== undefined
    ? welcomeSaleSavings
    : discount;
  const hasState = Boolean(selectedState);
  const isBihar = hasState && selectedState.toLowerCase() === 'bihar';
  const taxableAmount = Math.round((effectivePrice / 1.18) * 100) / 100;
  const taxAmount = Math.round((effectivePrice - taxableAmount) * 100) / 100;

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    mouseY.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };
  const handleMouseLeave = () => { setIsHovered(false); mouseX.set(0); mouseY.set(0); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative perspective-1000 group"
      style={{ rotateX: isHovered ? rotateX : 0, rotateY: isHovered ? rotateY : 0, transformStyle: 'preserve-3d' }}
    >
      <motion.div className={`absolute inset-0 rounded-[2.5rem] blur-[60px] transition-all duration-700 ${isHovered ? 'opacity-40 scale-110' : 'opacity-0 scale-90'} bg-gradient-to-br ${option.theme.gradient}`} style={{ x: isHovered ? glowX : 0, y: isHovered ? glowY : 0 }} />

      {(option.badge || totalSavings > 0 || welcomeSaleActive) && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full text-[9px] font-black tracking-widest uppercase shadow-lg ${
          welcomeSaleActive
            ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-purple-500/30 animate-pulse'
            : totalSavings > 0 
            ? 'bg-green-500 text-white' 
            : option.recommended || option.popular 
            ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-primary/30' 
            : 'bg-white dark:bg-glass/60 backdrop-blur-sm border border-black/10 dark:border-white/10 text-text-main'
        }`}>
          {welcomeSaleActive ? `🎉 Save ₹${totalSavings}` : totalSavings > 0 ? `Save ₹${totalSavings}` : option.badge}
        </div>
      )}

      <div className={`relative h-full bg-white/75 dark:bg-slate-900/30 backdrop-blur-3xl border rounded-[2rem] p-8 flex flex-col overflow-hidden transition-all duration-500 ${
        isHovered ? `shadow-2xl ${option.theme.glow}` : 'shadow-lg shadow-black/5 dark:shadow-none'
      } ${(option.recommended || option.popular || isActive) ? `${option.theme.border} ${option.theme.glow}` : `border-black/[0.08] dark:border-white/5`} ${
        isActive ? `ring-2 ring-primary ring-offset-4 ring-offset-background` : ''
      }`}>
        <div className="flex flex-col items-center mb-6 relative z-10">
          <div className={`w-12 h-12 rounded-xl bg-black/[0.03] dark:bg-white/5 flex items-center justify-center mb-4 border border-black/[0.06] dark:border-white/10 transition-transform duration-500 ${isHovered ? 'scale-110' : ''}`}>
            <option.theme.icon className={`w-6 h-6 ${option.theme.accent}`} />
          </div>
          <h3 className="text-xl font-bold text-text-main mb-1 text-center tracking-wide">{option.name}</h3>
          <p className="text-[10px] text-text-muted text-center font-bold uppercase tracking-[0.2em]">{option.duration} Access</p>
        </div>

        <div className="text-center mb-6 mt-auto relative z-10">
          <div className="relative inline-block">
            {welcomeSaleActive ? (
              <>
                <div className="text-5xl font-light text-emerald-600 dark:text-emerald-400">₹{effectivePrice}</div>
                <div className="absolute -top-3 -right-7 text-xs line-through text-text-muted opacity-60">{option.priceLabel}</div>
              </>
            ) : (
              <>
                <div className="text-5xl font-light text-text-main">{totalSavings > 0 ? `₹${effectivePrice}` : option.priceLabel}</div>
                {totalSavings > 0 && <div className="absolute -top-3 -right-7 text-xs line-through text-text-muted opacity-60">{option.priceLabel}</div>}
              </>
            )}
            <div className="text-text-muted text-[9px] mt-2 font-bold tracking-[0.3em] uppercase opacity-70">
              {option.id === 'lifetime' ? 'Perpetual' : `₹${(option.price / parseInt(option.durationLabel.split(' ')[0])).toFixed(1)} / day`}
            </div>
          </div>

          {/* Dynamic GST breakdown badge */}
          <div className="mt-4 flex flex-col items-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold shadow-sm">
              <span>₹{effectivePrice}</span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-normal">Inclusive of 18% GST</span>
            </div>
            <p className="text-[10px] text-text-muted mt-1.5 font-mono">
              {hasState
                ? `₹${taxableAmount.toFixed(2)} + ₹${taxAmount.toFixed(2)} ${isBihar ? 'CGST+SGST (9%+9%)' : 'IGST (18%)'}`
                : `Taxable: ₹${taxableAmount.toFixed(2)} | 18% GST: ₹${taxAmount.toFixed(2)}`}
            </p>
          </div>
        </div>

        {/* State Dropdown inside the Card */}
        <div className="w-full mb-5 text-left relative z-20">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider text-text-muted flex items-center gap-1">
              <span>Billing State</span>
            </label>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
              !hasState
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 font-bold'
                : 'bg-primary/10 text-primary border-primary/20'
            }`}>
              {!hasState ? 'Select State' : isBihar ? '10-Bihar' : 'Inter-State'}
            </span>
          </div>
          <div className="relative">
            <select
              value={selectedState}
              onChange={(e) => onStateChange(e.target.value)}
              className={`w-full bg-white dark:bg-slate-950/90 border ${
                !hasState ? 'border-amber-500/40 text-text-muted' : 'border-black/15 dark:border-white/10 text-text-main'
              } hover:border-primary/50 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-primary transition-colors cursor-pointer appearance-none pr-7 shadow-sm`}
            >
              <option value="" disabled className="bg-white dark:bg-slate-900 text-text-muted">
                -- Select Your State --
              </option>
              {INDIAN_STATES.map((st) => (
                <option key={st} value={st} className="bg-white dark:bg-slate-900 text-text-main">
                  {st}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted text-[10px]">
              ▼
            </div>
          </div>
        </div>

        <button
          onClick={() => onSelect(option.id)}
          disabled={isLoading || isActive || isDowngrade}
          className={`w-full py-4 rounded-xl font-bold text-xs uppercase tracking-[0.2em] relative z-30 transition-all duration-300 ${
            isLoading 
              ? 'opacity-70 cursor-wait' 
              : (isActive || isDowngrade) 
              ? 'opacity-50 cursor-not-allowed' 
              : 'cursor-pointer'
          } ${
            isActive 
              ? `bg-primary/10 text-primary border border-primary/30` 
              : isHovered && !isDowngrade 
              ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/25 scale-[1.02]' 
              : 'bg-black/[0.04] dark:bg-white/10 text-text-main border border-black/10 dark:border-white/10 hover:border-primary/40'
          }`}
        >
          <span className="flex items-center justify-center gap-2">
            {isLoading ? (<><svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Processing...</>) : (<>{isActive ? <Check className="w-4 h-4" /> : <Zap className="w-4 h-4" />}{isActive ? 'Active Plan' : isUpgrade ? 'Upgrade Now' : isDowngrade ? 'Current Tier' : 'Get Access'}</>)}
          </span>
        </button>
      </div>
    </motion.div>
  );
};

// ─── Main Pricing Page ────────────────────────────────────────────────────────
const Pricing: React.FC = () => {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');

  // DB dynamic rates state
  const [dbRates, setDbRates] = useState<Record<string, { bw: number; color: number }> | null>(null);

  // Fetch dynamic print rates from DB
  useEffect(() => {
    let mounted = true;
    apiGet<{ success: boolean; data: Record<string, { bw: number; color: number }> }>('/api/print/rates')
      .then((res: any) => {
        if (mounted && res.success && res.data) {
          setDbRates(res.data);
        }
      })
      .catch((err: any) => {
        console.error('Failed to fetch print rates from DB inside Pricing:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);
  const { isAuthenticated, signIn, user, hasActiveSubscription, subscription, refreshSubscription } = useAuth();
  const welcomeSale = useWelcomeSale();

  const pad = (n: number) => String(n).padStart(2, '0');

  const showToast_ = (msg: string) => { setToastMessage(msg); setShowToast(true); setTimeout(() => setShowToast(false), 3000); };
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const handleDownloadPassInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const res = await apiFetch('/api/payment/invoice/latest?format=html');
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Invoice not available' }));
        throw new Error(err.error || 'Failed to open invoice');
      }
      const html = await res.text();
      const win = window.open('', '_blank');
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
      }
    } catch (err: any) {
      showToast_(err.message || 'Invoice could not be loaded.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const getFrontendPlanId = (backendType: string) => ({ monthly: 'monthly', half_yearly: 'half-yearly', annual: 'yearly', lifetime: 'lifetime' }[backendType] || backendType);

  const getUpgradeDiscount = (planId: string) => {
    if (!hasActiveSubscription || !subscription) return 0;
    const currentPlanId = getFrontendPlanId(subscription.plan_type);
    const currentPlan = communityPassOptions.find(o => o.id === currentPlanId);
    if (!currentPlan || PLAN_RANKS[planId] <= PLAN_RANKS[currentPlanId]) return 0;
    const remainingDays = Math.max(0, (new Date(subscription.end_date).getTime() - Date.now()) / 86400000);
    const totalDays = currentPlanId === 'monthly' ? 30 : currentPlanId === 'half-yearly' ? 180 : 365;
    return Math.floor((currentPlan.price / totalDays) * remainingDays);
  };

  const loadRazorpayScript = (): Promise<void> => new Promise((resolve, reject) => {
    if ((window as any).Razorpay) return resolve();
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });

  const handleSelectPlan = async (planId: string) => {
    if (!selectedState) {
      showToast_('Please select your Billing State to proceed');
      return;
    }
    if (!isAuthenticated) { showToast_('Please sign in with Google to get access'); setTimeout(() => signIn(), 1500); return; }
    if (hasActiveSubscription && subscription) {
      const cur = getFrontendPlanId(subscription.plan_type);
      if (PLAN_RANKS[planId] < PLAN_RANKS[cur]) { setPaymentStatus('error'); setStatusMessage('You already have a higher tier Community Pass active.'); return; }
      if (PLAN_RANKS[planId] === PLAN_RANKS[cur]) { setPaymentStatus('error'); setStatusMessage(`You already have an active ${subscription.plan_type.replace('_', ' ')} Community Pass.`); return; }
    }
    const planTypeMap: Record<string, string> = { lifetime: 'lifetime', monthly: 'monthly', 'half-yearly': 'half_yearly', yearly: 'annual' };
    const planType = planTypeMap[planId];
    if (!planType) return;
    setLoadingPlan(planId); setPaymentStatus('idle'); setStatusMessage('');
    try {
      const res = await apiPost<{ success: boolean; data: { order_id: string; amount: number; currency: string; key_id: string; plan: any; prefill: { email: string } }; error?: string; message?: string }>('/api/payment/create-order', {
        plan_type: planType,
        state: selectedState,
        welcome_sale: welcomeSale.isActive,
      });
      if (!res.success) throw new Error(res.message || res.error || 'Failed to create order');
      const { order_id, amount, currency, key_id, plan, prefill } = res.data;
      await loadRazorpayScript();
      const rzp = new (window as any).Razorpay({
        key: key_id, amount: amount.toString(), currency, name: 'NotesCrafter', description: plan.name, order_id,
        handler: async (response: any) => {
          try {
            const v = await apiPost<{ success: boolean; message: string }>('/api/payment/verify', response);
            if (v.success) { setPaymentStatus('success'); setStatusMessage(v.message || 'Payment successful!'); refreshSubscription(); welcomeSale.markUsed(); }
            else { setPaymentStatus('error'); setStatusMessage('Verification failed. Contact support.'); }
          } catch { setPaymentStatus('error'); setStatusMessage('Verification failed. If deducted, contact support.'); }
        },
        prefill: { name: user?.name || '', email: prefill.email || user?.email || '' },
        theme: { color: '#A855F7' },
      });
      rzp.on('payment.failed', () => { setPaymentStatus('error'); setStatusMessage('Payment failed. Please try again.'); });
      rzp.open();
    } catch (err: any) { setPaymentStatus('error'); setStatusMessage(err.message || 'Something went wrong.'); }
    finally { setLoadingPlan(null); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">

        {paymentStatus !== 'idle' && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className={`max-w-4xl mx-auto mb-8 p-4 rounded-2xl border text-center font-medium shadow-sm ${paymentStatus === 'success' ? 'bg-green-500/10 dark:bg-green-500/20 border-green-500/30 text-green-700 dark:text-green-200 flex items-center justify-center gap-4 flex-wrap' : 'bg-red-500/10 dark:bg-red-500/20 border-red-500/30 text-red-700 dark:text-red-200'}`}>
            <span>{statusMessage}</span>
            {paymentStatus === 'success' && (
              <button
                onClick={handleDownloadPassInvoice}
                disabled={downloadingInvoice}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-green-600/15 hover:bg-green-600/25 dark:bg-green-500/30 dark:hover:bg-green-500/40 border border-green-600/30 dark:border-green-400/50 rounded-full text-xs font-bold text-green-900 dark:text-white transition-colors cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5" />
                {downloadingInvoice ? 'Opening...' : 'Download Tax Invoice'}
              </button>
            )}
          </motion.div>
        )}

        {hasActiveSubscription && subscription && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 p-4 rounded-2xl border bg-primary/10 border-primary/30 text-center mx-auto w-fit shadow-sm">
            <div className="text-text-main font-medium flex items-center gap-3 flex-wrap justify-center">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Check className="w-5 h-5 text-primary" />
                Active: <span className="text-primary font-bold capitalize">{subscription.plan_type.replace('_', ' ')} Pass</span>
                {subscription.remaining_days !== undefined && <span className="text-text-muted"> · {subscription.remaining_days} days left</span>}
              </span>
              <button
                onClick={handleDownloadPassInvoice}
                disabled={downloadingInvoice}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-black/[0.05] dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 border border-black/10 dark:border-white/20 rounded-full text-xs font-semibold text-text-main transition-colors cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5 text-primary" />
                {downloadingInvoice ? 'Opening...' : 'Tax Invoice'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Guest Welcome Sale Teaser — visible to visitors before signing in */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 200 }}
            className="relative mb-10 overflow-hidden rounded-2xl bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-amber-500/10 border border-purple-300/40 dark:border-purple-500/30 p-6 sm:p-7 flex flex-col sm:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-13 h-13 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25 shrink-0">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-black text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> First-Time Welcome Offer
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-extrabold text-text-main flex items-center gap-2">
                  Unlock 25% Off All Community Passes
                </h3>
                <p className="text-xs sm:text-sm text-text-muted mt-0.5">
                  Sign in to instantly start your exclusive 45-minute welcome discount on all pass tiers.
                </p>
              </div>
            </div>
            <button
              onClick={() => signIn()}
              className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider text-white bg-gradient-to-r from-purple-600 to-pink-500 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 active:scale-95 transition-all cursor-pointer"
            >
              Sign In to Claim 25% Off
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Welcome Sale Hero — only visible when the 45-min window is active */}
        {welcomeSale.isActive && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 200 }}
            className="relative mb-10 overflow-hidden rounded-2xl"
          >
            {/* Animated gradient border */}
            <div
              className="absolute inset-0 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(236,72,153,0.15) 50%, rgba(6,182,212,0.2) 100%)',
                border: '1px solid rgba(168,85,247,0.3)',
              }}
            />
            <motion.div
              className="absolute inset-0 pointer-events-none rounded-2xl"
              style={{
                background: 'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.06) 50%, transparent 80%)',
                backgroundSize: '200% 100%',
              }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ repeat: Infinity, duration: 3.5, ease: 'linear', repeatDelay: 1 }}
            />

            <div className="relative z-10 px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              {/* Left: Sale info */}
              <div className="flex items-center gap-4">
                <motion.div
                  animate={{ scale: [1, 1.15, 1], rotate: [0, -8, 8, 0] }}
                  transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}
                  className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center shadow-xl shadow-purple-500/30 shrink-0"
                >
                  <Gift className="w-7 h-7 text-white" />
                </motion.div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-black text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" />
                      </span>
                      Welcome Offer · Exclusive
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-text-main flex items-center gap-2">
                    <PartyPopper className="w-5 h-5 text-amber-500" />
                    25% Off All Passes!
                  </h2>
                  <p className="text-xs sm:text-sm text-text-muted mt-1">
                    Your one-time new-member discount · prices shown below are already discounted
                  </p>
                </div>
              </div>

              {/* Right: Countdown */}
              <div className="flex flex-col items-center shrink-0">
                <span className="text-[9px] uppercase tracking-widest font-black text-text-muted mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Offer expires in
                </span>
                <div className="flex items-center gap-1.5">
                  {[pad(welcomeSale.minutesLeft), pad(welcomeSale.secondsLeft)].map((val, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div
                        className={`flex flex-col items-center px-3 py-2 rounded-xl border shadow-sm transition-colors duration-500 ${
                          welcomeSale.minutesLeft < 5
                            ? 'bg-red-100/90 dark:bg-red-900/50 border-red-200 dark:border-red-500/30'
                            : welcomeSale.minutesLeft < 10
                            ? 'bg-amber-100/90 dark:bg-amber-900/50 border-amber-200 dark:border-amber-500/30'
                            : 'bg-purple-50/80 dark:bg-purple-900/30 border-purple-200 dark:border-purple-500/20'
                        }`}
                        style={{ minWidth: '3.2rem' }}
                      >
                        <motion.span
                          key={val}
                          initial={{ y: -6, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          className={`text-2xl sm:text-3xl font-black font-mono leading-none transition-colors duration-500 ${
                            welcomeSale.minutesLeft < 5
                              ? 'text-red-600 dark:text-red-400'
                              : welcomeSale.minutesLeft < 10
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-purple-700 dark:text-purple-300'
                          }`}
                        >
                          {val}
                        </motion.span>
                        <span className="text-[8px] text-text-muted font-bold uppercase tracking-widest mt-0.5">
                          {['MIN', 'SEC'][i]}
                        </span>
                      </div>
                      {i < 1 && (
                        <motion.span
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ repeat: Infinity, duration: 1 }}
                          className="text-xl font-black text-text-muted/40 -mt-3"
                        >
                          :
                        </motion.span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-text-main tracking-tight mb-4">Choose your <span className="font-bold">Pass</span></h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto mb-10 font-light">
            {welcomeSale.isActive
              ? <>Your exclusive <span className="text-purple-600 dark:text-purple-400 font-bold">25% welcome discount</span> is applied to all prices below.</>
              : 'Support NotesCrafter and unlock unlimited processing power.'
            }
          </p>
          <div className="flex flex-wrap items-center justify-center gap-10 text-text-muted opacity-80">
            {[{ icon: Sparkles, label: 'Premium Features' }, { icon: Shield, label: 'Secure Checkout' }, { icon: Zap, label: 'Instant Access' }].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3"><Icon className="w-4 h-4 text-primary" /><span className="text-[11px] uppercase tracking-widest font-bold">{label}</span></div>
            ))}
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {communityPassOptions.map((option, index) => {
            const currentPlanId = subscription ? getFrontendPlanId(subscription.plan_type) : null;
            const isActive = hasActiveSubscription && currentPlanId === option.id;
            const isUpgrade = hasActiveSubscription && !isActive && PLAN_RANKS[option.id] > (PLAN_RANKS[currentPlanId || ''] || 0);
            const isDowngrade = hasActiveSubscription && !isActive && !isUpgrade;
            const discount = isUpgrade ? getUpgradeDiscount(option.id) : 0;
            return (
              <PricingCard
                key={option.id}
                option={option}
                index={index}
                isActive={isActive}
                isUpgrade={isUpgrade}
                isDowngrade={isDowngrade}
                discount={discount}
                isLoading={loadingPlan === option.id}
                selectedState={selectedState}
                onStateChange={setSelectedState}
                onSelect={handleSelectPlan}
                welcomeSaleActive={welcomeSale.isActive}
                welcomeSalePrice={welcomeSale.getDiscountedPrice(option.price)}
                welcomeSaleSavings={welcomeSale.getSavings(option.price)}
              />
            );
          })}
        </div>

        {/* Comparison Table */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="max-w-7xl mx-auto mb-20 px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-text-main mb-4">Pass & Benefits <span className="font-bold">Comparison</span></h2>
            <p className="text-text-muted font-light max-w-xl mx-auto">Compare each Community Pass tier and see the exclusive product offers you unlock.</p>
          </div>
          <div className="overflow-x-auto pt-4 pb-8">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="py-8 px-5 text-[10px] font-black uppercase tracking-[0.3em] text-text-muted border-b border-black/10 dark:border-white/10 min-w-[180px]">Tier Details</th>
                  
                  <th className="py-8 px-5 border-b border-black/10 dark:border-white/10 bg-slate-500/5 rounded-t-2xl min-w-[150px] text-center">
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-1">Guest</div>
                    <span className="text-xl font-bold text-text-main block">Free</span>
                    <span className="text-[9px] text-text-muted font-medium mt-1 block">Session Based</span>
                  </th>

                  <th className="py-8 px-5 border-b border-black/10 dark:border-white/10 bg-slate-400/10 dark:bg-slate-400/5 rounded-t-2xl min-w-[160px] text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <Shield className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300">Silver Pass</span>
                    </div>
                    <span className="text-xl font-bold text-text-main block">₹39</span>
                    <span className="text-[9px] text-text-muted font-medium mt-1 block">30 Days (Inc. GST)</span>
                  </th>

                  <th className="py-8 px-5 border-b border-black/10 dark:border-white/10 bg-amber-500/10 dark:bg-amber-500/5 rounded-t-2xl min-w-[160px] text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <Crown className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">Gold Pass</span>
                    </div>
                    <span className="text-xl font-bold text-text-main block">₹179</span>
                    <span className="text-[9px] text-amber-800/80 dark:text-amber-300/60 font-medium mt-1 block">6 Months (Inc. GST)</span>
                  </th>

                  <th className="py-8 px-5 border-b border-black/10 dark:border-white/10 bg-indigo-500/10 dark:bg-indigo-500/5 rounded-t-2xl min-w-[160px] text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <Zap className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-700 dark:text-indigo-300">Platinum Pass</span>
                    </div>
                    <span className="text-xl font-bold text-text-main block">₹299</span>
                    <span className="text-[9px] text-indigo-800/80 dark:text-indigo-300/60 font-medium mt-1 block">1 Year (Inc. GST)</span>
                  </th>

                  <th className="py-8 px-5 border border-b-0 border-cyan-500/40 dark:border-cyan-500/30 bg-cyan-500/15 dark:bg-cyan-500/10 rounded-t-2xl min-w-[170px] text-center relative shadow-[0_-15px_30px_-5px_rgba(6,182,212,0.1)]">
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-black text-[8px] uppercase tracking-[0.2em] px-2.5 py-0.5 rounded-full shadow-lg border border-cyan-400/30 whitespace-nowrap">Best Value</span>
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <Gem className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 animate-pulse" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300">Diamond Pass</span>
                    </div>
                    <span className="text-xl font-bold text-text-main block">₹789</span>
                    <span className="text-[9px] text-cyan-800/80 dark:text-cyan-300/60 font-medium mt-1 block">Lifetime (Inc. GST)</span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {(() => {
                  const getBwRate = (key: string, defaultRate: number) => {
                    return dbRates ? dbRates[key]?.bw : defaultRate;
                  };

                  const getColorRate = (key: string, defaultRate: number) => {
                    return dbRates ? dbRates[key]?.color : defaultRate;
                  };

                  const getPrintsLabel = (plan: string, prefix: string, defaultBw: number, defaultCol: number, suffix = '') => {
                    const bw = getBwRate(plan, defaultBw)?.toFixed(2);
                    const col = getColorRate(plan, defaultCol)?.toFixed(2);
                    return `${prefix} (B&W: ₹${bw}, Color: ₹${col})${suffix}`;
                  };

                  return [
                    {
                      subtext: 'FLOW',
                      subColor: 'text-purple-600 dark:text-purple-400',
                      href: 'https://flow.notescrafter.com',
                      guest: 'Standard speed • Up to 5 MB per file limit',
                      silver: 'Fast speed • No file size limits',
                      gold: 'Priority queue • No file size limits',
                      platinum: 'Priority queue • Multi-file batch uploads • Unlimited',
                      diamond: 'Ultra-priority queue • Multi-file batch uploads • VIP Beta Access',
                    },
                    {
                      subtext: 'PRINTS',
                      subColor: 'text-indigo-600 dark:text-indigo-400',
                      href: 'https://print.notescrafter.com',
                      guest: getPrintsLabel('guest', 'Standard rate', 1.25, 3.35),
                      silver: getPrintsLabel('monthly', 'Save 8%', 1.15, 3.00),
                      gold: getPrintsLabel('half_yearly', 'Save 17%', 1.05, 2.75),
                      platinum: getPrintsLabel('annual', 'Save 25%', 0.99, 2.35),
                      diamond: getPrintsLabel('lifetime', 'Save 35%', 0.89, 1.99, ' — Best Price!'),
                    },
                  {
                    subtext: 'TOOLS',
                    subColor: 'text-teal-600 dark:text-teal-400',
                    href: 'https://tools.notescrafter.com',
                    guest: 'Completely Free for everyone • No restrictions',
                    silver: 'Completely Free for everyone • No restrictions',
                    gold: 'Completely Free for everyone • No restrictions',
                    platinum: 'Completely Free for everyone • No restrictions',
                    diamond: 'Completely Free for everyone • No restrictions',
                  },
                  {
                    subtext: 'ANDROID APP',
                    subColor: 'text-emerald-600 dark:text-emerald-400',
                    href: 'https://play.google.com/store/apps/details?id=com.notescrafter.app',
                    guest: 'Completely Free mobile access • Standard speed',
                    silver: 'Syncs pass benefits on mobile • Ad-free performance',
                    gold: 'Syncs pass benefits on mobile • Ad-free performance',
                    platinum: 'Syncs pass benefits on mobile • Ad-free performance',
                    diamond: 'Syncs pass benefits on mobile • Ad-free performance',
                    },
                  ];
                })().map((row, i) => (
                  <tr key={i} className="group transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                    <td className="py-5 px-5 border-b border-black/[0.06] dark:border-white/5">
                      <a
                        href={row.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-start leading-none py-1 group/link transition-all duration-200 hover:scale-105 opacity-80 hover:opacity-100 cursor-pointer text-left"
                      >
                        <span className="text-sm sm:text-base font-bold text-text-main transition-opacity">
                          NotesCrafter
                        </span>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`text-[8px] sm:text-[9px] font-bold tracking-[0.2em] uppercase transition-colors ${row.subColor}`}>
                            {row.subtext}
                          </span>
                          <ExternalLink size={10} className="text-text-muted group-hover/link:text-primary transition-colors" />
                        </div>
                      </a>
                    </td>
                    <td className="py-5 px-5 text-slate-700 dark:text-slate-300/80 font-light leading-relaxed border-b border-black/[0.06] dark:border-white/5 bg-slate-500/[0.03] dark:bg-slate-500/[0.02] text-center">{row.guest}</td>
                    <td className="py-5 px-5 text-slate-700 dark:text-slate-200/80 font-light leading-relaxed border-b border-black/[0.06] dark:border-white/5 bg-slate-400/[0.04] dark:bg-slate-400/[0.02] text-center">{row.silver}</td>
                    <td className="py-5 px-5 text-amber-900 dark:text-amber-200/80 font-light leading-relaxed border-b border-black/[0.06] dark:border-white/5 bg-amber-500/[0.04] dark:bg-amber-500/[0.02] text-center">{row.gold}</td>
                    <td className="py-5 px-5 text-indigo-900 dark:text-indigo-200/80 font-light leading-relaxed border-b border-black/[0.06] dark:border-white/5 bg-indigo-500/[0.04] dark:bg-indigo-500/[0.02] text-center">{row.platinum}</td>
                    <td className="py-5 px-5 text-cyan-950 dark:text-cyan-200/90 font-medium leading-relaxed border-x border-b border-cyan-500/20 bg-cyan-500/[0.08] dark:bg-cyan-500/[0.05] text-center shadow-[0_15px_30px_-5px_rgba(6,182,212,0.05)]">{row.diamond}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Why Support */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }} className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 dark:border-primary/30 rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="text-center mb-8">
              <Rocket className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-text-main mb-3">Why Your Support Matters</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[{ icon: Rocket, title: 'Fund Development', desc: 'New features, improvements, and innovations' }, { icon: Monitor, title: 'Cover Costs', desc: 'Hosting, domains, and infrastructure' }, { icon: Heart, title: 'Stay Independent', desc: 'No ads, no tracking, no compromises' }].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="text-center bg-white/80 dark:bg-glass/10 backdrop-blur-sm border border-black/5 dark:border-white/5 rounded-2xl p-6 shadow-sm">
                  <Icon className="w-12 h-12 text-primary mx-auto mb-3" />
                  <h3 className="font-bold text-text-main mb-2">{title}</h3>
                  <p className="text-sm text-text-muted">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Important Info */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.9 }} className="max-w-4xl mx-auto mb-12">
          <div className="bg-white/80 dark:bg-glass/10 backdrop-blur-sm border border-black/10 dark:border-border-glow rounded-2xl p-6 shadow-sm">
            <h3 className="text-xl font-bold text-text-main mb-4 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" />Important Information</h3>
            <div className="space-y-3 text-text-muted">
              {[
                { title: 'No Refund Policy:', body: 'All Community Pass purchases are final and non-refundable.' },
                { title: 'Automatic Expiry:', body: 'Your pass expires at the end of your chosen duration.' },
                { title: 'Privacy First:', body: 'All PDF processing happens in your browser. Your documents never touch our servers.' },
                { title: 'Lifetime Access:', body: 'The Lifetime plan means you never worry about renewals or price increases again.' },
              ].map(({ title, body }) => (
                <div key={title} className="flex items-start gap-3">
                  <span className="text-primary font-bold">•</span>
                  <p><span className="font-semibold text-text-main">{title}</span> {body}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 1.1 }} className="text-center">
          <p className="text-text-muted">
            Have questions?{' '}
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: 'help-center' } }))} className="text-primary hover:text-secondary underline font-semibold">Help Center</button>
            {' '}or{' '}
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: 'contact-us' } }))} className="text-primary hover:text-secondary underline font-semibold">contact us</button>
          </p>
        </motion.div>

        {showToast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-primary to-secondary text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">{toastMessage}</span>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Pricing;
